import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import type {JOB_INFORMATION} from 'src/types/crm';

import {
  BodyMedium,
  BodyMediumBold,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {converJobTypeString, getExperience, getRangeLabel} from 'src/utils/crm';
import {useInflight} from 'src/hooks/redux';

import {useSelector, useDispatch} from 'react-redux';
import {
  selectJobInformation,
  selectLocations,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';
import {getJobInformation} from 'src/action-creators/crm';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import css from './basic-job-details.css';


export const BasicJobDetailsTab = ({jobId}: {jobId?: string}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserInfo);
  const viewJobSalary = selectHasCrmPermission(
    userDetails,
    CRM_PERMISSIONS.VIEW_JOB_SALARY,
  );
  React.useEffect(() => {
    if (jobId && Number(jobId) !== Number(jobInfo?.jobId)) {
      dispatch(getJobInformation(jobId));
    }
  }, [jobId]);

  const locations = useSelector(selectLocations);
  const {organization: organizationInfo, user: userInfo} =
    useSelector(selectUserInfo) ?? {};
  const findOfficeLocation = (officeId: any) => {
    if (!locations) {
      return '-';
    }
    const officeName =
      // @ts-expect-error - TS2339 - Property 'find' does not exist on type 'JOB_INFORMATION'. | TS7006 - Parameter 'item' implicitly has an 'any' type.
      locations.find((item) => item.id === officeId)?.name || '-';
    return officeName;
  };

  const getSalaryRange = (jobInfo: any) => {
    if (jobInfo.salaryEnd === 0) {
      return '-';
    }
    return `${jobInfo.salaryStart || 0}-${jobInfo.salaryEnd} ${
      jobInfo.salaryCurrency || ''
    }`.trim();
  };

  const getDepartments = (departments: any) => {
    if (!departments) {
      return [];
    }
    // @ts-expect-error - TS7006 - Parameter 'department' implicitly has an 'any' type.
    return departments.map((department) => ({
      label: department.levelName,
      value: department.name || '-',
      id: department.id,
    }));
  };

  const tableEntries = [
    jobInfo?.code && {label: t('Id', 'Id'), value: jobInfo.code},
    {
      label: t('Job_Title', 'Job Title'),
      value: jobInfo.title || '-',
      id: 'title',
    },
    {
      label: t('Employment_Type', 'Employment Type'),
      value: converJobTypeString(jobInfo.jobType),
      id: 'empType',
    },
    {
      label: t('Open_Positions', 'Open Positions'),
      value: jobInfo.openPositions,
      id: 'positionCount',
    },
    {
      label: t('Job_Experience_years', 'Job Experience (years)'),
      value: getExperience(jobInfo.experienceStart, jobInfo.experienceEnd),
      id: 'experience',
    },
    viewJobSalary
      ? {
          label: getRangeLabel(jobInfo.employeeType.toLowerCase() || '-'),
          value: getSalaryRange(jobInfo),
          id: 'salaryRange',
        }
      : null,
    ...getDepartments(jobInfo.departments),
    {
      label: t('Grade', 'Grade'),
      value: jobInfo?.gradeName || 'NA',
      id: 'grade',
    },
    {
      label: t('Office_Location', 'Office Location'),
      value: findOfficeLocation(jobInfo?.officeId),
      id: 'officeLocation',
    },
    {
      label: t('Location_External', 'Location (External)'),
      value: jobInfo?.location,
      id: 'locationExternal',
    },
  ].filter(Boolean);

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableName = ({data}) => (
    <BasicSingleCell>
      <BodySmall color="secondary">{data.label}</BodySmall>
    </BasicSingleCell>
  );

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableValue = ({data}) => (
    <BasicSingleCell>
      <BodyMediumBold>{data.value}</BodyMediumBold>
    </BasicSingleCell>
  );

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: BasicTableValue,
        },
      ]}
    />
  );
};
